/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import { inject as service } from '@ember/service';
import { computed } from '@ember/object';
import Component from '@glimmer/component';
import FormCondition from 'cio/app-models/conditions/form';
import FacebookLeadAdsService from 'cio/services/facebook-lead-ads';
import FacebookLeadAdsForm from 'cio/models/facebook-lead-ads-form';
import { FormType, formTypeIcon, formTypeIconDefaultFill, SharedFormModelInterface } from 'cio/utils/forms';
import CustomFormsService from 'cio/services/custom-forms';
import CustomForm from 'cio/models/custom-form';
import { sortByCaseInsensitive } from 'cio/utils/sort';

interface ConditionEditorFormComponentArgs {
  condition: FormCondition;
  disabled: boolean;
  selectedFormID: string;
  selectedFormType: FormType;
  errorMessage?: string;
  size?: string;
  showFormsToConnect?: boolean;
  addNewFormAction: () => unknown;
  alreadySelectedForms?: (FacebookLeadAdsForm | CustomForm)[] | null;
}

type SelectableFormType = {
  formType: FormType;
  formId: string;
  formIdLowercase: string;
  label: string;
  icon: string;
  iconFill: string;
};

function convertToSelectableType(form: SharedFormModelInterface): SelectableFormType {
  return {
    formType: form.formType,
    formId: form.formId,
    formIdLowercase: form.formIdLowercase,
    label: form.name,
    icon: formTypeIcon(form.formType),
    iconFill: formTypeIconDefaultFill(form.formType),
  };
}

export default class ConditionEditorFormComponent extends Component<ConditionEditorFormComponentArgs> {
  @service facebookLeadAds!: FacebookLeadAdsService;
  @service customForms!: CustomFormsService;

  get selectedForm() {
    return this.formsForSelect.findBy('formIdLowercase', this.args.selectedFormID);
  }

  @computed(
    'args.{showFormsToConnect,selectedFormID,alreadySelectedForms.[]}',
    'facebookLeadAds.{connectedForms,connectableDisconnectedForms}.[]',
    'customForms.{disconnectedForms,connectedOrDraftForms}.[]',
  )
  get formsForSelect(): SelectableFormType[] {
    let fbForms: FacebookLeadAdsForm[] = [
      ...(this.args.showFormsToConnect
        ? this.facebookLeadAds.connectableDisconnectedForms
        : this.facebookLeadAds.connectedForms),
    ];

    let customForms: CustomForm[] = [...this.customForms.connectedOrDraftForms];
    let formsToShow = [
      ...fbForms.map((fb) => convertToSelectableType(fb)),
      ...customForms.map((cf) => convertToSelectableType(cf)),
    ];

    let selectedForm: SelectableFormType | null = null;
    if (this.args.selectedFormID) {
      let selected: FacebookLeadAdsForm | CustomForm | undefined;
      if (this.args.selectedFormType === FormType.FacebookLeadAds) {
        selected = this.facebookLeadAds.forms.findBy('formIdLowercase', this.args.selectedFormID);
      } else {
        selected = this.customForms.forms.findBy('formIdLowercase', this.args.selectedFormID);
      }

      selectedForm = selected ? convertToSelectableType(selected) : null;
    }

    if (selectedForm && !formsToShow.some((form) => form.formId === selectedForm?.formId)) {
      // always show the currently selected form, even if not active (edge case, but avoids broken empty input)
      formsToShow.unshift(selectedForm);
    }
    // block selecting forms already selected in other form inputs:
    if (this.args.alreadySelectedForms?.length) {
      formsToShow = formsToShow.filter(
        ({ formId }) =>
          !this.args.alreadySelectedForms?.some((f) => f.formId === formId) ||
          formId.toLowerCase() === this.args.selectedFormID,
      );
    }

    return sortByCaseInsensitive(formsToShow, ['label']);
  }
}
